<script setup lang="ts">
import Slider from '@vueform/slider';
import '@vueform/slider/themes/default.css';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFilterRangeProps {
  inverse?: boolean;
  label?: string;
  marker?: string;
  max?: number;
  min?: number;
  multiplier?: number;
  name?: string;
  range?: boolean;
  step?: number;
  tooltip?: string;
  value?: number | number[];
}
const props = withDefaults(defineProps<CiFilterRangeProps>(), {
  inverse: true,
  label: '',
  marker: '{value}',
  max: 100,
  min: 0,
  multiplier: 1,
  name: '',
  range: false,
  step: 1,
  tooltip: '',
  value: 0,
});

const emit = defineEmits<{
  input: [value: number | number[]];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext, interpolate } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const rangeValue = ref(getRangeValue());
const sliderRef = ref<InstanceType<typeof Slider> | null>(null);
const tooltipPlacement = ref<null | 'top' | 'bottom' | 'left' | 'right'>('bottom');
const tooltipVisible = ref(!!props.value);

/********************
 * WATCHER           *
 ********************/
watch(
  () => props.value,
  () => {
    tooltipVisible.value = !!props.value;
    rangeValue.value = getRangeValue();
  },
);
watch(() => rangeValue.value, onChange);

/********************
 * FUNCTIONS         *
 ********************/
function onChange() {
  let val = toRaw(rangeValue.value);
  if (!props.range) {
    val = parseInt(val, 10);
  }
  if (val > 0 && props.multiplier > 1) {
    val /= props.multiplier;
  }
  emit('input', val);
}

function getRangeValue() {
  let val = props.value;
  if (!val) {
    val = props.range ? [props.min, props.max] : props.min;
  }
  if (props.multiplier && typeof val === 'number') {
    val *= props.multiplier;
  }
  return toRaw(val);
}

function onDragStart() {
  sliderRef.value.slider.noUiSlider.updateOptions(
    {
      tooltips: { to: tooltipText },
    },
    false,
  );

  [...sliderRef.value.slider.querySelectorAll('.slider-tooltip')].forEach((ele) => {
    ele.classList.remove('slider-tooltip-bottom');
    ele.classList.add('slider-tooltip-top');
  });
}

function onDragEnd(value: number | number[]) {
  if (value === props.min) {
    sliderRef.value.slider.noUiSlider.updateOptions(
      {
        tooltips: false,
      },
      false,
    );
  } else {
    sliderRef.value.slider.noUiSlider.updateOptions(
      {
        tooltips: { to: tooltipText },
      },
      false,
    );
  }
  tooltipPlacement.value = 'bottom';
}

function formatString(str, value) {
  const params = { value };
  return str.replace(/\{([^}]+)\}/g, (_a, b) => params[b]);
}

function tooltipText(value) {
  if (props.tooltip) {
    const text = props.tooltip.replace(/\{([^}]+)\}/g, '%{$1}');
    return interpolate(text, { value });
  }
  return $gettext('%{value} and more', { value });
}
</script>

<template>
  <div class="filter-range m-0 mb-6 flex break-inside-avoid">
    <div class="grow">
      <label
        :for="name"
        class="filter-range__label text-input-book m-0 mb-2 block"
      >{{ label }}</label>
      <Slider
        ref="sliderRef"
        v-model="rangeValue"
        class="filter-range__input"
        :format="tooltipText"
        :max="props.max"
        :min="props.min"
        :step="props.step"
        :tooltip-position="tooltipPlacement"
        :lazy="true"
        :tooltips="tooltipVisible"
        @start="onDragStart"
        @change="onDragEnd"
      />
      <div class="mt-2 flex justify-between text-sm">
        <div>{{ formatString(props.marker, props.min) }}</div>
        <div>{{ formatString(props.marker, props.max) }}</div>
      </div>
    </div>
  </div>
</template>

<style>
.slider-target,
.slider-target * {
  touch-action: pan-y !important;
}
</style>
